import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import DashboardModal from "./DashboardModal";
import { getSharedPlaying } from "./playingBus";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide1Mobile from "./HTLDSlide1Mobile";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide1Content, useSlide1, Seg } from "./slide1.data";

const accent = "#378add";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root: {
  background: "#040f18", fontFamily: font, color: "#d0e8f8",
  width: "100%", height: "100%",
  display: "flex", flexDirection: "column",
  boxSizing: "border-box", overflow: "hidden",
},
subtitleBar: {
  background: "#050f1a", borderBottom: "1px solid rgba(55,138,221,0.1)",
  padding: "clamp(8px,1vw,14px) clamp(20px,3vw,48px)",
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
  flex: 1, minHeight: 0,
  display: "flex", gap: "clamp(20px,2.6vw,44px)",
  padding: "clamp(14px,1.8vw,26px) clamp(20px,3vw,48px) clamp(14px,1.8vw,24px)",
  alignItems: "stretch", overflow: "hidden",
},
content: {
  flex: 1, minWidth: "min(100%, 380px)",
  display: "flex", flexDirection: "column",
  justifyContent: "space-between",
  gap: "clamp(8px,1vw,16px)", overflow: "hidden",
},
panel: {
  width: "clamp(300px,34vw,540px)", flexShrink: 0,
  display: "flex", flexDirection: "column",
},
};

// ─── Eye of Awareness Canvas ──────────────────────────────────────────────────
const EyeCanvas: React.FC = () => {
const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);

useEffect(() => {
  const canvas = canvasRef.current; if (!canvas) return;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
  const W = 420, H = 560; canvas.width = W; canvas.height = H;
  const caption = pick(slide1Content).canvas.caption; // slides remount on language change

  const ECX = 210, ECY = 242;
  const ELX = 28,  ERX = 392;
  const TC1B = { x: 118, y: 86  }, TC2B = { x: 302, y: 86  };
  const BC1B = { x: 118, y: 372 }, BC2B = { x: 302, y: 372 };
  const IRIS_R = 82, PUPIL_R_BASE = 40;

  let frame = 0;

  const bgStars = Array.from({ length: 140 }, () => ({
    x: Math.random() * W, y: Math.random() * H,
    r: 0.3 + Math.random() * 1.8,
    tw: Math.random() * Math.PI * 2,
    speed: 0.012 + Math.random() * 0.028,
  }));

  const irisStars = Array.from({ length: 70 }, () => {
    const a = Math.random() * Math.PI * 2;
    const r = (PUPIL_R_BASE + 8) + Math.random() * (IRIS_R - PUPIL_R_BASE - 10);
    return { x: ECX + r * Math.cos(a), y: ECY + r * Math.sin(a), r: 0.6 + Math.random() * 2.2, tw: Math.random() * Math.PI * 2 };
  });

  const pupilStars = Array.from({ length: 28 }, () => {
    const a = Math.random() * Math.PI * 2;
    const r = 3 + Math.random() * (PUPIL_R_BASE - 7);
    return { x: ECX + r * Math.cos(a), y: ECY + r * Math.sin(a), r: 0.6 + Math.random() * 1.6, tw: Math.random() * Math.PI * 2 };
  });

  const fibers = Array.from({ length: 52 }, (_, i) => ({
    angle: (i / 52) * Math.PI * 2 + (Math.random() - 0.5) * 0.10,
    len: 0.4 + Math.random() * 0.55,
    alpha: 0.18 + Math.random() * 0.32,
  }));

  const particles = Array.from({ length: 45 }, () => ({
    angle:  Math.random() * Math.PI * 2,
    radius: IRIS_R + 35 + Math.random() * 200,
    speed:  (0.002 + Math.random() * 0.005) * (Math.random() < 0.5 ? 1 : -1),
    r:      0.6 + Math.random() * 2.2,
    alpha:  0.35 + Math.random() * 0.65,
    tw:     Math.random() * Math.PI * 2,
    tws:    0.018 + Math.random() * 0.038,
    col:    (["255,255,255","120,200,255","200,150,255","80,220,200"] as const)[Math.floor(Math.random() * 4)],
  }));

  function getBlinkAmount(_f: number) { return 1; }

  type Pt = { x: number; y: number };
  function buildEyePath(tc1: Pt, tc2: Pt, bc1: Pt, bc2: Pt) {
    ctx.beginPath();
    ctx.moveTo(ELX, ECY);
    ctx.bezierCurveTo(tc1.x, tc1.y, tc2.x, tc2.y, ERX, ECY);
    ctx.bezierCurveTo(bc1.x, bc1.y, bc2.x, bc2.y, ELX, ECY);
    ctx.closePath();
  }
  function upperPt(t: number, tc1: Pt, tc2: Pt): Pt {
    const m = 1 - t;
    return {
      x: m*m*m*ELX + 3*m*m*t*tc1.x + 3*m*t*t*tc2.x + t*t*t*ERX,
      y: m*m*m*ECY + 3*m*m*t*tc1.y + 3*m*t*t*tc2.y + t*t*t*ECY,
    };
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);

    const pulse      = 0.5 + 0.5 * Math.sin(frame * 0.022);
    const slowPulse  = 0.5 + 0.5 * Math.sin(frame * 0.010);
    const rot        = frame * 0.0052;
    const blink      = getBlinkAmount(frame);
    const PUPIL_R    = PUPIL_R_BASE + 7 * Math.sin(frame * 0.018);

    const TC1 = { x: TC1B.x, y: ECY - (ECY - TC1B.y) * blink };
    const TC2 = { x: TC2B.x, y: ECY - (ECY - TC2B.y) * blink };
    const BC1 = { x: BC1B.x, y: ECY + (BC1B.y - ECY) * blink };
    const BC2 = { x: BC2B.x, y: ECY + (BC2B.y - ECY) * blink };

    const bgGrd = ctx.createRadialGradient(ECX, ECY, 0, ECX, ECY, W);
    bgGrd.addColorStop(0, "#09091f"); bgGrd.addColorStop(0.45, "#040610"); bgGrd.addColorStop(1, "#010208");
    ctx.fillStyle = bgGrd; ctx.fillRect(0, 0, W, H);

    [
      { x: 55,  y: 65,  r: 160, c: "rgba(90,15,200,0.40)"  },
      { x: 365, y: 95,  r: 140, c: "rgba(15,65,210,0.35)"  },
      { x: 38,  y: 435, r: 175, c: "rgba(110,10,170,0.32)" },
      { x: 388, y: 425, r: 148, c: "rgba(10,85,220,0.30)"  },
      { x: 210, y: 35,  r: 115, c: "rgba(85,8,220,0.26)"   },
      { x: 210, y: 525, r: 128, c: "rgba(15,110,230,0.28)" },
      { x: 115, y: 285, r: 100, c: "rgba(180,15,220,0.22)" },
      { x: 315, y: 285, r: 100, c: "rgba(10,180,240,0.22)" },
    ].forEach(n => {
      const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
      g.addColorStop(0, n.c); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
      ctx.fillStyle = g; ctx.fill();
    });

    bgStars.forEach(s => {
      const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * s.speed + s.tw));
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${(tw * 0.80).toFixed(2)})`; ctx.fill();
    });

    particles.forEach(p => {
      p.angle += p.speed;
      const x = ECX + p.radius * Math.cos(p.angle);
      const y = ECY + p.radius * Math.sin(p.angle) * 0.55;
      const tw = 0.35 + 0.65 * Math.abs(Math.sin(frame * p.tws + p.tw));
      const a = p.alpha * tw;
      const g = ctx.createRadialGradient(x, y, 0, x, y, p.r * 4);
      g.addColorStop(0, `rgba(${p.col},${a})`); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath(); ctx.arc(x, y, p.r * 4, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();
      ctx.beginPath(); ctx.arc(x, y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${p.col},${a})`; ctx.fill();
    });

    const RAY_COUNT = 16;
    for (let i = 0; i < RAY_COUNT; i++) {
      const angle  = (i / RAY_COUNT) * Math.PI * 2 + rot * 0.08;
      const isLong = i % 4 === 0, isMed = i % 4 === 1, isShort = i % 4 === 2;
      const rayLen  = isLong ? 260 : isMed ? 180 : isShort ? 120 : 80;
      const rayBase = isLong ? 28  : isMed ? 18  : isShort ? 12  : 7;
      const ex = ECX + rayLen * Math.cos(angle);
      const ey = ECY + rayLen * Math.sin(angle);
      const peakA = (isLong ? 0.38 + 0.12 * pulse : isMed ? 0.22 + 0.08 * pulse : isShort ? 0.13 + 0.05 * pulse : 0.07 + 0.03 * pulse) * blink;
      const rg = ctx.createLinearGradient(ECX, ECY, ex, ey);
      rg.addColorStop(0,   `rgba(200,235,255,${peakA})`);
      rg.addColorStop(0.2, `rgba(100,180,255,${peakA * 0.70})`);
      rg.addColorStop(0.55,`rgba(80,60,220,${peakA * 0.28})`);
      rg.addColorStop(1,   "rgba(55,138,221,0)");
      const px = ECX - Math.sin(angle) * rayBase, py = ECY + Math.cos(angle) * rayBase;
      const px2= ECX + Math.sin(angle) * rayBase, py2= ECY - Math.cos(angle) * rayBase;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(ex, ey); ctx.lineTo(px2, py2);
      ctx.closePath(); ctx.fillStyle = rg; ctx.fill();
    }

    const outerGlow = ctx.createRadialGradient(ECX, ECY, IRIS_R * 0.65, ECX, ECY, IRIS_R + 150);
    outerGlow.addColorStop(0,    `rgba(120,200,255,${(0.85 + 0.15 * pulse) * blink})`);
    outerGlow.addColorStop(0.2,  `rgba(70,130,250,${(0.55 + 0.18 * pulse) * blink})`);
    outerGlow.addColorStop(0.55, `rgba(110,40,210,${(0.25 + 0.08 * pulse) * blink})`);
    outerGlow.addColorStop(1,    "rgba(55,138,221,0)");
    ctx.beginPath(); ctx.ellipse(ECX, ECY, IRIS_R + 150, IRIS_R + 115, 0, 0, Math.PI * 2);
    ctx.fillStyle = outerGlow; ctx.fill();

    const bloom = ctx.createRadialGradient(ECX, ECY, IRIS_R * 0.5, ECX, ECY, IRIS_R + 80);
    bloom.addColorStop(0,   `rgba(220,80,255,${(0.22 + 0.10 * slowPulse) * blink})`);
    bloom.addColorStop(0.5, `rgba(80,40,200,${(0.12 + 0.05 * slowPulse) * blink})`);
    bloom.addColorStop(1,   "rgba(0,0,0,0)");
    ctx.beginPath(); ctx.ellipse(ECX, ECY, IRIS_R + 80, IRIS_R + 60, 0, 0, Math.PI * 2);
    ctx.fillStyle = bloom; ctx.fill();

    ctx.save();
    buildEyePath(TC1, TC2, BC1, BC2); ctx.clip();

    buildEyePath(TC1, TC2, BC1, BC2);
    const spaceBg = ctx.createRadialGradient(ECX, ECY - 20, 0, ECX, ECY, 195);
    spaceBg.addColorStop(0, "#0c061e"); spaceBg.addColorStop(0.55, "#07031a"); spaceBg.addColorStop(1, "#020112");
    ctx.fillStyle = spaceBg; ctx.fill();

    [
      { x: ECX-30, y: ECY-25, r: 115, c: "rgba(130,25,230,0.65)" },
      { x: ECX+40, y: ECY+15, r: 100, c: "rgba(15,100,230,0.58)" },
      { x: ECX-10, y: ECY+35, r:  85, c: "rgba(0,200,250,0.45)"  },
      { x: ECX+22, y: ECY-40, r:  80, c: "rgba(200,35,250,0.42)" },
      { x: ECX,    y: ECY,    r:  70, c: "rgba(50,150,255,0.38)" },
      { x: ECX-55, y: ECY+12, r:  60, c: "rgba(0,240,190,0.28)" },
      { x: ECX+50, y: ECY-20, r:  55, c: "rgba(255,80,180,0.18)"},
    ].forEach(n => {
      const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
      g.addColorStop(0, n.c); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
      ctx.fillStyle = g; ctx.fill();
    });

    const irisRim = ctx.createRadialGradient(ECX, ECY, IRIS_R * 0.78, ECX, ECY, IRIS_R);
    irisRim.addColorStop(0,   "rgba(55,138,221,0)");
    irisRim.addColorStop(0.45,`rgba(120,210,255,${0.52 + 0.20 * pulse})`);
    irisRim.addColorStop(1,   `rgba(200,248,255,${0.88 + 0.12 * pulse})`);
    ctx.beginPath(); ctx.arc(ECX, ECY, IRIS_R, 0, Math.PI * 2);
    ctx.fillStyle = irisRim; ctx.fill();

    ctx.save(); ctx.translate(ECX, ECY); ctx.rotate(rot);
    ctx.beginPath(); ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2); ctx.clip();
    for (let i = 0; i < 28; i++) {
      const a1 = (i / 28) * Math.PI * 2, a2 = ((i + 0.65) / 28) * Math.PI * 2;
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, IRIS_R - 1, a1, a2); ctx.closePath();
      const c = ["rgba(55,138,221,","rgba(130,45,245,","rgba(0,190,250,","rgba(200,55,230,"][i % 4];
      ctx.fillStyle = `${c}${0.12 + (i % 3) * 0.07})`; ctx.fill();
    }
    ctx.restore();

    ctx.save(); ctx.translate(ECX, ECY);
    ctx.beginPath(); ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2); ctx.clip();
    const sa = frame * 0.028;
    const sg = ctx.createLinearGradient(
      Math.cos(sa) * IRIS_R, Math.sin(sa) * IRIS_R,
      Math.cos(sa + Math.PI) * IRIS_R, Math.sin(sa + Math.PI) * IRIS_R,
    );
    sg.addColorStop(0,    "rgba(220,248,255,0)");
    sg.addColorStop(0.44, "rgba(220,248,255,0)");
    sg.addColorStop(0.50, `rgba(220,248,255,${0.22 + 0.10 * pulse})`);
    sg.addColorStop(0.56, "rgba(220,248,255,0)");
    sg.addColorStop(1,    "rgba(220,248,255,0)");
    ctx.fillStyle = sg; ctx.fillRect(-IRIS_R, -IRIS_R, IRIS_R * 2, IRIS_R * 2);
    ctx.restore();

    ctx.save(); ctx.translate(ECX, ECY); ctx.rotate(-rot * 0.6);
    ctx.beginPath(); ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2); ctx.clip();
    fibers.forEach(f => {
      const x1 = Math.cos(f.angle) * PUPIL_R_BASE, y1 = Math.sin(f.angle) * PUPIL_R_BASE;
      const x2 = Math.cos(f.angle) * (PUPIL_R_BASE + (IRIS_R - PUPIL_R_BASE) * f.len);
      const y2 = Math.sin(f.angle) * (PUPIL_R_BASE + (IRIS_R - PUPIL_R_BASE) * f.len);
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = `rgba(180,228,255,${f.alpha})`; ctx.lineWidth = 0.9; ctx.stroke();
    });
    ctx.restore();

    const irisGlow = ctx.createRadialGradient(ECX, ECY, PUPIL_R, ECX, ECY, IRIS_R);
    irisGlow.addColorStop(0, `rgba(110,210,255,${0.42 + 0.16 * pulse})`);
    irisGlow.addColorStop(1, "rgba(80,180,255,0)");
    ctx.beginPath(); ctx.arc(ECX, ECY, IRIS_R, 0, Math.PI * 2);
    ctx.fillStyle = irisGlow; ctx.fill();

    ctx.beginPath(); ctx.arc(ECX, ECY, IRIS_R, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(140,235,255,${0.45 + 0.14 * pulse})`; ctx.lineWidth = 9; ctx.stroke();
    ctx.strokeStyle = `rgba(200,248,255,${0.92 + 0.08 * pulse})`; ctx.lineWidth = 3; ctx.stroke();

    irisStars.forEach(s => {
      const d = Math.hypot(s.x - ECX, s.y - ECY);
      if (d > PUPIL_R_BASE + 2 && d < IRIS_R - 2) {
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * 0.028 + s.tw));
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(tw * 0.95).toFixed(2)})`; ctx.fill();
      }
    });

    const pupilGrd = ctx.createRadialGradient(ECX - 9, ECY - 9, 0, ECX, ECY, PUPIL_R);
    pupilGrd.addColorStop(0, "#0a0520"); pupilGrd.addColorStop(0.65, "#060312"); pupilGrd.addColorStop(1, "#020009");
    ctx.beginPath(); ctx.arc(ECX, ECY, PUPIL_R, 0, Math.PI * 2);
    ctx.fillStyle = pupilGrd; ctx.fill();

    ctx.save(); ctx.translate(ECX, ECY); ctx.rotate(rot * 3.2);
    for (let arm = 0; arm < 3; arm++) {
      const aOff = (arm / 3) * Math.PI * 2;
      ctx.beginPath();
      for (let r = 4; r < PUPIL_R; r += 1.4) {
        const a = aOff + r * 0.20, x = r * Math.cos(a), y = r * Math.sin(a);
        r <= 4 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(120,200,255,${(0.35 + 0.14 * pulse).toFixed(2)})`;
      ctx.lineWidth = 2.2; ctx.stroke();
    }
    for (let arm = 0; arm < 5; arm++) {
      const aOff = (arm / 5) * Math.PI * 2;
      ctx.beginPath();
      for (let r = 2; r < PUPIL_R * 0.65; r += 1) {
        const a = aOff + r * 0.32, x = r * Math.cos(a), y = r * Math.sin(a);
        r <= 2 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(180,230,255,${(0.20 + 0.08 * pulse).toFixed(2)})`;
      ctx.lineWidth = 1; ctx.stroke();
    }
    ctx.restore();

    pupilStars.forEach(s => {
      if (Math.hypot(s.x - ECX, s.y - ECY) < PUPIL_R - 1) {
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame * 0.032 + s.tw));
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230,245,255,${(tw * 0.95).toFixed(2)})`; ctx.fill();
      }
    });

    const pNeb = ctx.createRadialGradient(ECX - 14, ECY - 10, 0, ECX, ECY, PUPIL_R);
    pNeb.addColorStop(0,   `rgba(140,55,230,${0.38 + 0.14 * pulse})`);
    pNeb.addColorStop(0.5, "rgba(60,120,210,0.22)");
    pNeb.addColorStop(1,   "rgba(0,0,0,0)");
    ctx.beginPath(); ctx.arc(ECX, ECY, PUPIL_R, 0, Math.PI * 2);
    ctx.fillStyle = pNeb; ctx.fill();

    const coreGrd = ctx.createRadialGradient(ECX, ECY, 0, ECX, ECY, 32);
    coreGrd.addColorStop(0,    "rgba(255,255,255,1.0)");
    coreGrd.addColorStop(0.18, `rgba(210,248,255,${0.92 + 0.07 * pulse})`);
    coreGrd.addColorStop(0.45, `rgba(90,190,255,${0.65 + 0.22 * pulse})`);
    coreGrd.addColorStop(0.80, `rgba(55,138,221,${0.28 + 0.12 * pulse})`);
    coreGrd.addColorStop(1,    "rgba(55,138,221,0)");
    ctx.beginPath(); ctx.arc(ECX, ECY, 32, 0, Math.PI * 2); ctx.fillStyle = coreGrd; ctx.fill();
    ctx.beginPath(); ctx.arc(ECX, ECY, 5.5 + pulse * 3, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,1.0)"; ctx.fill();

    ctx.save(); ctx.translate(ECX, ECY); ctx.rotate(slowPulse * 0.3);
    for (let i = 0; i < 8; i++) {
      const a   = (i / 8) * Math.PI * 2;
      const len = i % 2 === 0 ? 22 : 12;
      const alp = i % 2 === 0 ? 0.80 + 0.18 * pulse : 0.45 + 0.12 * pulse;
      ctx.strokeStyle = `rgba(230,252,255,${alp})`; ctx.lineWidth = i % 2 === 0 ? 2 : 1.2;
      ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(a) * len, Math.sin(a) * len); ctx.stroke();
    }
    ctx.restore();

    ctx.beginPath(); ctx.ellipse(ECX-34, ECY-36, 24, 13, -0.5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.32)"; ctx.fill();
    ctx.beginPath(); ctx.ellipse(ECX-27, ECY-28, 11, 6.5, -0.5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.60)"; ctx.fill();
    ctx.beginPath(); ctx.ellipse(ECX+25, ECY+23, 8, 4.5, 0.6, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.25)"; ctx.fill();

    if (blink < 0.98) {
      const lidA = (1 - blink) * 0.92;
      ctx.save(); buildEyePath(TC1, TC2, BC1, BC2); ctx.clip();
      const lg = ctx.createLinearGradient(ECX, ECY - 40, ECX, ECY + 40);
      lg.addColorStop(0,   `rgba(6,3,20,${lidA})`);
      lg.addColorStop(0.6, `rgba(6,3,20,${lidA * 0.6})`);
      lg.addColorStop(1,   "rgba(6,3,20,0)");
      ctx.fillStyle = lg; ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }

    ctx.restore();

    ctx.lineCap = "round";

    ctx.beginPath(); ctx.moveTo(ELX, ECY); ctx.bezierCurveTo(TC1.x, TC1.y, TC2.x, TC2.y, ERX, ECY);
    ctx.strokeStyle = `rgba(180,240,255,${0.18 + 0.08 * pulse})`; ctx.lineWidth = 22; ctx.stroke();
    ctx.strokeStyle = `rgba(120,210,255,${0.55 + 0.20 * pulse})`; ctx.lineWidth = 9;  ctx.stroke();
    ctx.strokeStyle = `rgba(230,252,255,${0.95 + 0.05 * pulse})`; ctx.lineWidth = 2.8;ctx.stroke();

    ctx.beginPath(); ctx.moveTo(ELX, ECY); ctx.bezierCurveTo(BC1.x, BC1.y, BC2.x, BC2.y, ERX, ECY);
    ctx.strokeStyle = `rgba(55,138,221,${0.65 + 0.18 * pulse})`; ctx.lineWidth = 2.5; ctx.stroke();
    ctx.strokeStyle = `rgba(120,200,255,${0.30 + 0.12 * pulse})`; ctx.lineWidth = 9;  ctx.stroke();

    [ELX, ERX].forEach(cx => {
      const dg = ctx.createRadialGradient(cx, ECY, 0, cx, ECY, 30);
      dg.addColorStop(0,   `rgba(220,250,255,${0.95 + 0.05 * pulse})`);
      dg.addColorStop(0.35,`rgba(120,220,255,${0.60 + 0.18 * pulse})`);
      dg.addColorStop(1,   "rgba(120,220,255,0)");
      ctx.beginPath(); ctx.arc(cx, ECY, 30, 0, Math.PI * 2); ctx.fillStyle = dg; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, ECY, 6, 0, Math.PI * 2); ctx.fillStyle = "rgba(240,255,255,1.0)"; ctx.fill();
    });

    for (let t = 0.025; t <= 0.975; t += 0.058) {
      const p  = upperPt(t, TC1, TC2);
      const d  = Math.hypot(p.x - ECX, p.y - ECY) || 1;
      const nx = (p.x - ECX) / d, ny = (p.y - ECY) / d;
      const ll = 12 + 9 * Math.sin(t * Math.PI);
      const la = 0.42 + 0.20 * Math.sin(t * Math.PI);
      ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + nx * ll * 2.0, p.y + ny * ll * 2.0);
      ctx.strokeStyle = `rgba(60,160,255,${la * 0.30})`; ctx.lineWidth = 6; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + nx * ll * 1.4, p.y + ny * ll * 1.4);
      ctx.strokeStyle = `rgba(160,228,255,${la * 0.60})`; ctx.lineWidth = 2.8; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + nx * ll, p.y + ny * ll);
      ctx.strokeStyle = `rgba(220,248,255,${la})`; ctx.lineWidth = 1.5; ctx.stroke();
    }

    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.font = "11px 'Space Grotesk',Arial,sans-serif";
    ctx.fillStyle = `rgba(120,195,255,${0.45 + 0.15 * pulse})`;
    ctx.fillText(caption, ECX, H - 14);

    frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
  }

  drawFnRef.current = draw;
  return () => { drawFnRef.current = null; };
}, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

return (
  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <canvas
      ref={canvasRef}
      style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
    />
  </div>
);
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide1Inner: React.FC = () => {
const { meta, whatItIs, approach, notList, stats } = useSlide1();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((sg, i) =>
    sg.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{sg.t}</span>
    : sg.em ? <em key={i}>{sg.t}</em>
    : <React.Fragment key={i}>{sg.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);
const [showDashboard, setShowDashboard] = useState(false);

// ── Font & scrollbar styles ──────────────────────────────────────────────
useEffect(() => {
  if (!document.querySelector('link[href*="Space+Grotesk"]')) {
    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
    document.head.appendChild(link);
    fontLinkRef.current = link;
  }

  if (!document.getElementById("htld-slide1-noscroll")) {
    const style = document.createElement("style");
    style.id = "htld-slide1-noscroll";
    style.textContent = `
      .htld-s1-body::-webkit-scrollbar { display: none; }
      .htld-s1-body { scrollbar-width: none; -ms-overflow-style: none; }
    `;
    document.head.appendChild(style);
  }

  return () => {
    if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
      document.head.removeChild(fontLinkRef.current);
      fontLinkRef.current = null;
    }
    const s = document.getElementById("htld-slide1-noscroll");
    if (s && document.head.contains(s)) document.head.removeChild(s);
  };
}, []);

// ── Pause / resume slideshow when the dashboard modal opens / closes ─────
const prevModalRef = useRef<boolean>(false);
const slideshowWasPlayingRef = useRef<boolean>(false);
useEffect(() => {
  const wasOpen = prevModalRef.current;
  prevModalRef.current = showDashboard;
  if (showDashboard && !wasOpen) {
    slideshowWasPlayingRef.current = getSharedPlaying();
    window.dispatchEvent(new Event("yogyism:play"));
  } else if (!showDashboard && wasOpen) {
    if (slideshowWasPlayingRef.current) {
      window.dispatchEvent(new Event("yogyism:resume"));
    }
  }
}, [showDashboard]);

const lbl: CSSProperties = {
  fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
  color: accent, letterSpacing: 3, textTransform: "uppercase",
  marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
  fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", fontFamily: FONT_BODY,
  lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null);

return (
  <div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
    <SideStarField rootRef={rootRef} />   {/* add this line */}

    {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
    <div style={S.subtitleBar}>
      <div style={{
        fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal",
        color: "#7aa8cc", fontFamily: FONT_BODY,
      }}>
        {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
        <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
      </div>
      <div style={{
        fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
        color: accent, fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
        whiteSpace: "nowrap",
      }}>
        {meta.headerTitle}
      </div>
    </div>

    {/* Body */}
    <div style={S.body}>

      {/* Content */}
      <div style={S.content}>

        {/* Title */}
        <div>
          <h1 style={{
            fontFamily: FONT_DISPLAY,
            fontSize: "clamp(22px,2.9vw,40px)", fontStyle: "normal", fontWeight: 600,
            color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2,
          }}>
            {meta.titlePlain} <span style={{ color: accent }}>{meta.titleAccent}</span>
          </h1>
          <p style={{
            fontFamily: FONT_BODY,
            fontSize: "clamp(12px,1.15vw,16px)", fontStyle: "normal", fontWeight: 300,
            color: "#7aa8cc", margin: 0, letterSpacing: 0.3,
          }}>
            {meta.subtitle}
          </p>
        </div>

        {/* What it is */}
        <div style={{ borderBottom: "1px solid rgba(55,138,221,0.10)", paddingBottom: "clamp(8px,1.1vw,15px)" }}>
          <div style={lbl}>{meta.whatItIsLabel}</div>
          <p style={txt}>{renderSegs(whatItIs, "#dceeff")}</p>
        </div>

        {/* The unique approach */}
        <div style={{
          background: "rgba(55,138,221,0.07)",
          border: "1px solid rgba(55,138,221,0.20)",
          borderRadius: 8,
          padding: "clamp(9px,1.1vw,15px) clamp(11px,1.3vw,18px)",
        }}>
          <div style={lbl}>{meta.approachLabel}</div>
          <p style={{ ...txt, color: "#d0e8ff" }}>{renderSegs(approach, "#ffffff")}</p>
        </div>

        {/* What it is not */}
        <div>
          <div style={lbl}>{meta.notLabel}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(4px,0.55vw,10px)" }}>
            {notList.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex", gap: "clamp(7px,0.75vw,11px)", alignItems: "flex-start",
                  background: "rgba(55,138,221,0.03)",
                  border: "1px solid rgba(55,138,221,0.08)",
                  borderRadius: 6,
                  padding: "clamp(5px,0.6vw,9px) clamp(7px,0.8vw,12px)",
                }}
              >
                <span style={{ color: "rgba(220,90,90,0.80)", flexShrink: 0, fontSize: "clamp(10px,0.9vw,13px)", marginTop: 2 }}>✕</span>
                <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(10px,0.88vw,13px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.55, color: "#9bbdd6" }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", flexWrap: "nowrap", gap: "clamp(6px,0.75vw,11px)" }}>
          {stats.map((f, i) => (
            <div
              key={i}
              style={{
                flex: "1 1 0%", minWidth: "0",
                background: "rgba(55,138,221,0.05)",
                border: "1px solid rgba(55,138,221,0.12)",
                borderRadius: 6,
                padding: "clamp(6px,0.7vw,11px) clamp(6px,0.7vw,11px)",
                textAlign: "center",
              }}
            >
              <div style={{
                fontSize: "clamp(7px,0.6vw,9px)", fontFamily: FONT_MONO,
                color: "rgba(55,138,221,0.55)", letterSpacing: 2, marginBottom: 3,
              }}>
                {f.label.toUpperCase()}
              </div>
              <div style={{
                fontFamily: FONT_BODY, fontSize: "clamp(10px,0.95vw,14px)",
                fontStyle: "normal", fontWeight: 700, color: "#c8dff5",
              }}>
                {f.value}
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Panel — Eye of Awareness animation fills the full panel height */}
      <div style={S.panel}>
        <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <EyeCanvas />
        </div>
      </div>

    </div>

    {/* Modal (dashboard stays in the slide; login lives in the toolbar) */}
    <DashboardModal isOpen={showDashboard} onClose={() => setShowDashboard(false)} />

  </div>
);
};

// ─── Main Slide ───────────────────────────────────────────────────────────────
// Scaled as one unit so it looks identical across resolutions / zoom / window size.
const HTLDSlide1: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide1Mobile />
    : <ScaleToFit><HTLDSlide1Inner /></ScaleToFit>;
};

export default HTLDSlide1;